/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  THE ONLY FILE TO EDIT PER TENANT.
 *
 *  Multi-tenant here means one file, edited and rebuilt per customer
 *  deployment -- not one running instance serving many tenants at once.
 *  Swap the values below for a new customer, then run:
 *      pnpm theme:build     regenerates src/theme/tokens.css
 *      pnpm test contrast   checks every pairing in BOTH themes
 *
 *  The suite names any pairing that falls below its threshold, so a palette
 *  that is illegible somewhere tells you exactly where. Fix, ship.
 *
 *  Do not add design tokens here. Semantic tokens are derived in
 *  src/theme/tokens.ts from `colors` below; everything else (name, logo,
 *  support contact) is read directly from this file by whatever renders it
 *  (`AppShell`'s sidebar header, `index.html`'s runtime title/favicon).
 *
 *  OKLCH rather than hex because lightness is perceptual: a 0.1 step means the
 *  same apparent change at every hue. That is what makes the mechanical
 *  derivation in tokens.ts trustworthy -- the same recipe applied to a blue
 *  brand and a red brand produces equally legible scales.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface Oklch {
  /** 0 = black, 1 = white. Perceptual. */
  l: number
  /** 0 = grey. ~0.37 is the most saturated displayable. */
  c: number
  /** Hue angle, 0-360. */
  h: number
}

export interface BrandColors {
  /** Primary actions, active navigation (light theme). Painted as given. */
  primary: Oklch
  /**
   * The primary as the dark theme paints it. A deep brand blue disappears on
   * a dark ground, so the dark theme lifts it rather than deriving it.
   */
  primaryDark: Oklch
  /** Accents, selected states, focus ring, lead chart series. */
  secondary: Oklch
  /**
   * The third brand-identity hue -- a second accent distinct from
   * `secondary`, used for icon badges and chart series that need to read as
   * "also this brand" without competing with the primary action colour.
   */
  tertiary: Oklch
  /**
   * The chromatic base for every surface and text token. Carries a trace of
   * `primary`'s hue on purpose: a pure grey beside a warm brand reads as a
   * different material, while a near-grey sharing the hue reads as family.
   */
  neutral: Oklch
  /*
   * State colours -- deliberately NOT derived from the brand hues above.
   * "Failed" must read as failure whatever the tenant's brand turns out to
   * be, and a brand whose primary is red must not make every success badge
   * look like an error. Same four across every tenant unless a tenant
   * explicitly asks otherwise.
   */
  success: Oklch
  warning: Oklch
  danger: Oklch
  info: Oklch
  /*
   * Payment-operator tints for the operator badges (src/brand/operators/,
   * LLD §4.3). Badge backgrounds only: never a chart series (LLD §4.2) and
   * never a state. Bank transfer has no operator colour; its badge is drawn
   * from the neutral family.
   */
  /** MTN Mobile Money yellow. */
  mtn: Oklch
  /** Orange Money orange. */
  orange: Oklch
}

export interface BrandConfig {
  /** Full product name -- sidebar header, <title>, export/email headers later. */
  name: string
  /** Short fallback shown wherever the logo can't render. */
  shortName: string
  /**
   * An image asset for the logo. When unset, `name` renders as text -- never
   * a broken image, never blank. (No inline-SVG option: DevSMS renders no raw
   * markup anywhere, LLD §3.4.)
   */
  logoAssetPath?: string
  /**
   * A single-colour mark drawn before the name (alpha only: the wordmark
   * paints it with the primary token, so it follows the theme). Ignored when
   * `logoAssetPath` is set.
   */
  markAssetPath?: string
  /** `<link rel="icon">` target. Omitted entirely (not a dead reference) when unset. */
  faviconAssetPath?: string
  /**
   * The trailing part of `name` that the wordmark draws in the primary colour
   * ("Dev" + "SMS"). Ignored unless `name` ends with it.
   */
  wordmarkAccent?: string
  colors: BrandColors
  support?: { email?: string; url?: string }
}

export const BRAND: BrandConfig = {
  name: "DevSMS",
  shortName: "DevSMS",
  wordmarkAccent: "SMS",
  markAssetPath: "/logo-mark.svg",
  faviconAssetPath: "/favicon.svg",

  colors: {
    /** Hiis deep blue, #0014c9 (web/fork/brand.css), converted exactly (DW-43). */
    primary: { l: 0.388, c: 0.255, h: 264.2 },
    /**
     * Hiis blue lifted for dark ground, ≈ #667eff (brand-300, itself L 0.641).
     * L 0.64, not the brief's 0.63: at 0.63 a link on surface-raised measured
     * 4.40:1 (DW-43).
     */
    primaryDark: { l: 0.64, c: 0.2, h: 268 },
    /** A lighter Hiis blue. */
    secondary: { l: 0.62, c: 0.19, h: 266 },
    /** Teal. */
    tertiary: { l: 0.7, c: 0.12, h: 190 },
    /** Cool grey, tracing the blue primary. */
    neutral: { l: 0.55, c: 0.015, h: 266 },

    success: { l: 0.55, c: 0.115, h: 148 },
    warning: { l: 0.71, c: 0.14, h: 75 },
    danger: { l: 0.53, c: 0.19, h: 27 },
    info: { l: 0.58, c: 0.115, h: 245 },

    mtn: { l: 0.87, c: 0.18, h: 90 },
    orange: { l: 0.72, c: 0.19, h: 50 },
  },
}
